/**
 * Reglas puras del cambio de recurso sobre una posición YA asignada.
 *
 * La decisión de fondo la toma el servidor (`replace_assignment`, migración 0018): esta
 * lógica existe para que la pantalla pueda apagar el botón y —sobre todo— para decirle al
 * coordinador QUÉ va a pasar antes de que confirme, no después de que el servidor lo rechace.
 */

/** Estados en los que una asignación está viva y por lo tanto se puede reemplazar. */
export type EstadoAsignacionVigente = 'propuesta' | 'aprobada';

export interface EstadoPosicionAsignada {
  /** Estado de la asignación vigente sobre la posición. */
  estado: EstadoAsignacionVigente;
  /** Marcas de asistencia (`attendance`) colgadas de esa asignación: ingreso y/o salida. */
  marcas: number;
  /** `positions.status = 'cerrada'`: el periodo ya se liquidó. */
  posicionCerrada: boolean;
}

export interface VeredictoCambio {
  permitido: boolean;
  /** Por qué no se puede. Se muestra tal cual; nombra la causa real, no "no se permite". */
  motivo?: string;
  /** El camino correcto para lo que la persona está intentando resolver. */
  salida?: string;
}

/**
 * ¿Se puede cambiar el recurso de esta posición?
 *
 * Dos bloqueos, en este orden:
 * 1. **Posición cerrada** — el periodo está liquidado y su histórico es inmutable. Va
 *    primero porque, si además hay marcado, el cierre es la explicación que manda: aunque
 *    se borrara la evidencia, seguiría sin poder tocarse.
 * 2. **Ya hay asistencia marcada** — la foto sellada, el GPS y la hora son de esa persona.
 *    Cambiar el nombre encima dejaría la evidencia de una a nombre de otra, y el cierre
 *    pagaría a quien no trabajó. La corrección va por Validación, no por reasignación.
 */
export function puedeCambiarRecurso(p: EstadoPosicionAsignada): VeredictoCambio {
  if (p.posicionCerrada) {
    return {
      permitido: false,
      motivo: 'La posición está cerrada: pertenece a un periodo ya liquidado.',
      salida: 'Un cierre aprobado no se edita. Pide al jefe de operaciones un ajuste del periodo.',
    };
  }
  if (p.marcas > 0) {
    const marcas = p.marcas === 1 ? '1 marca' : `${p.marcas} marcas`;
    return {
      permitido: false,
      motivo:
        `Esta posición ya tiene ${marcas} de asistencia con foto y GPS a nombre del recurso ` +
        'actual. Si le cambias la persona, esa evidencia queda a nombre de alguien que no ' +
        'estuvo ahí y el cierre le pagaría el día.',
      salida:
        'Corrige el turno desde Validación: observa el marcado con una nota, o registra la ' +
        'salida si se fue sin marcar. Si el turno lo terminó otra persona, va como una ' +
        'posición aparte.',
    };
  }
  return { permitido: true };
}

export interface DatosAviso {
  estado: EstadoAsignacionVigente;
  recursoActual: string;
  recursoNuevo: string;
}

/**
 * Lo que hay que decirle al coordinador ANTES de confirmar.
 *
 * El caso que duele es el de la asignación **aprobada**: el reemplazo nace en `propuesta`,
 * así que entre la confirmación y la aprobación del jefe el turno queda sin nadie que pueda
 * marcar. Si eso pasa la mañana de la activación, el PDV se queda sin persona y sin registro.
 * Por eso se dice con esas palabras y no con un genérico "requiere aprobación".
 */
export function avisosDeReemplazo({ estado, recursoActual, recursoNuevo }: DatosAviso): string[] {
  const avisos: string[] = [];
  if (estado === 'aprobada') {
    avisos.push(
      `La asignación de ${recursoActual} está aprobada. El reemplazo de ${recursoNuevo} entra ` +
      'como propuesta y necesita otra vez la aprobación del jefe de operaciones.',
    );
    avisos.push(
      'Mientras no la apruebe, el turno queda sin persona activa: nadie puede marcar ' +
      'asistencia en esa posición.',
    );
    avisos.push(
      `${recursoActual} deja de ver el turno en su celular apenas confirmes.`,
    );
  } else {
    avisos.push(
      `La propuesta de ${recursoActual} todavía no estaba aprobada: se reemplaza por una ` +
      `propuesta nueva a nombre de ${recursoNuevo}, que también espera al jefe de operaciones.`,
    );
  }
  avisos.push('El cambio queda en la auditoría: quién lo hizo, a quién sacó y a quién puso.');
  return avisos;
}
